
import { textSearch } from "@/assets/ts/_utils";
import { toLocaleDateString } from "@/assets/ts/_utils/formatters";
import JsonExcel from "@/components/widgets/tables/JsonExcel.vue";
import { edadesMenores } from "@/core/data/edadesMenores";
import { generos } from "@/core/data/generos";
import { municipiosComarcas } from "@/core/data/municipiosProvinciasComarcas";
import { SortProps, sortFn } from "@/core/helpers/sort";
import { FormularioEstado, ServicioEstado } from "@/enums";
import { ServicioComun } from "@/store/models";
import { AuthModule, OrganismosModule } from "@/store/modules";
import { ErrorMessage, useField } from "vee-validate";
import { computed, defineComponent, ref, watch } from "vue";
import { useArrayPagination } from "vue-composable";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";
import { useAdminComarca } from "@/composable/useAdminComarca";

export default defineComponent({
  name: "servicios-solicitados-tabla",
  components: { "download-excel": JsonExcel, ErrorMessage },
  emits: ["comarca-change"],
  props: {
    servicios: Object as () => ServicioComun[] | null,
  },
  methods: {
    tableRowClassName({ row }) {
      if (row.familiaPrioritaria) {
        return "warning-row";
      }
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const authModule = getModule(AuthModule, store);
    const comarcaId = authModule.user.comarcaId;

    const organismosModule = getModule(OrganismosModule, store);
    const { selectedComarcaId } = useAdminComarca();
    const loadingComarcas = ref(false);

    const ensureComarcasLoaded = async () => {
      if (organismosModule.organismosListado.length === 0) {
        loadingComarcas.value = true;
        try {
          await organismosModule.fetchAll();
        } finally {
          loadingComarcas.value = false;
        }
      }
    };

    const comarcas = computed(() => {
      const list = organismosModule.organismosListado.map((item) => ({
        id: item.id,
        nombre: item.nombre,
      }));
      list.unshift({ id: "TODAS", nombre: "Todas" });
      return list;
    });

    const onComarcaVisibleChange = (open: boolean) => {
      if (open && !comarcaId) {
        ensureComarcasLoaded();
      }
    };

    const onComarcaChange = () => {
      emit("comarca-change");
    };

    const csvLabels = {
      tipoServicioValor: t("csv.solicitudes.servicio.tipoServicioValor"),
      nombreServicio: t("csv.solicitudes.servicio.nombreServicio"),
      nombreComarca: t("csv.solicitudes.servicio.comarca"),
      fecha: t("csv.solicitudes.servicio.fecha"),
      responsable: t("csv.solicitudes.servicio.responsable"),
      dni: t("csv.solicitudes.servicio.dni"),
      telefono: t("csv.solicitudes.servicio.telefono"),
      menores: t("csv.solicitudes.servicio.menores"),
      ingresos: t("csv.solicitudes.servicio.ingresos"),
      familiaPrioritaria: t("csv.solicitudes.servicio.familiaPrioritaria"),
      estadoValor: t("csv.solicitudes.servicio.estado"),
      localidad: t("csv.solicitudes.servicio.localidad"),
    };

    const csvFields = [
      "tipoServicioValor",
      "nombreServicio",
      ...(!comarcaId ? ["nombreComarca"] : []),
      "fecha",
      "responsable",
      "dni",
      "telefono",
      "localidad",
      "menores",
      "ingresos",
      "familiaPrioritaria",
      "estadoValor",
    ];
    const csvName = "serviciosListado" + new Date().getTime() + ".xlsx";

    const search = ref("");
    const estado = ref<string | null>(FormularioEstado.pendiente); // valor por defecto "Pendiente"

    const handleEdit = (index, row) => {
      if (row.tipoServicio === "SERVICIO_PROGRAMADO") {
        router.push(`/listados/servicios-programados-solicitudes/${row.id}`);
      } else {
        router.push(`/listados/servicios/${row.id}`);
      }
    };

    const sortEstado = function (a, b) {
      if (a.estado < b.estado) return -1;
      if (a.estado > b.estado) return 1;
      return 0;
    };
    const sortIngresos = function (a, b) {
      let primero = isNaN(a.ingresos) ? 0 : a.ingresos;
      let segundo = isNaN(b.ingresos) ? 0 : b.ingresos;

      return primero - segundo;
    };

    watch(
      () => props.servicios,
      () => {
        currentPage.value = 1;
      }
    );

    const servicios = computed(() => {
      // NOTA: Necesario para exportar los campos
      return props.servicios?.map((ele) => {
        // calcular edad de manera robusta
        let edad: number | null = null;
        if (ele?.fechaNacimiento) {
          const dob = new Date(ele.fechaNacimiento);
          if (!isNaN(dob.getTime())) {
            const today = new Date();
            edad = today.getFullYear() - dob.getFullYear();
            const m = today.getMonth() - dob.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
              edad--;
            }
          }
        }
        return {
          ...ele,
          edad,
          nombreComarca: ele.comarca?.nombre,
          fecha: new Date(ele.createdOn).toLocaleDateString("es-ES"),
        };
      });
    });

    const sortProps = ref<SortProps>({
      prop: "createdOn",
      order: "descending",
      method: null,
    });

    const filteredCollection = computed(() =>
      (servicios.value ?? [])
        .slice()
        .sort(sortFn(sortProps))
        .filter((data) => {
          const matchesSearch =
            !search.value ||
            textSearch(!comarcaId ? data.nombreComarca : "", search.value) ||
            textSearch(data.responsable, search.value) ||
            textSearch(data.dni, search.value) ||
            textSearch(data.telefono, search.value) ||
            textSearch(data.menores, search.value) ||
            textSearch(data.nombreServicio, search.value) ||
            textSearch(data.localidad, search.value);
          const matchesEstado = !estado.value || data.estado === estado.value;

          return matchesSearch && matchesEstado;
        })
    );

    const { value: localidad } = useField("localidad");
    const { value: filtroFechas } = useField("filtroFechas");
    const { value: genero } = useField("genero");
    const { value: edad } = useField("edad");

    const serviciosListado = computed(() => {
      const filteredBySearch = (servicios.value ?? [])
        .slice()
        .sort(sortFn(sortProps))
        .filter(
          (data) =>
            !search.value ||
            textSearch(!comarcaId ? data.nombreComarca : "", search.value) ||
            textSearch(data.responsable, search.value) ||
            textSearch(data.dni, search.value) ||
            textSearch(data.telefono, search.value) ||
            textSearch(data.menores, search.value) ||
            textSearch(data.nombreServicio, search.value) ||
            textSearch(data.localidad, search.value)
        );

      // empezamos con el resultado filtrado por búsqueda y luego aplicamos localidad/fechas/estado
      let result = filteredBySearch;

      if (genero?.value !== undefined && genero?.value !== "") {
        result = result?.filter((data) =>
          servicios.value
            ?.filter((data) => data.menorTratamiento == genero.value)
            ?.includes(data)
        );
      }

      if (edad?.value !== undefined && edad?.value !== "") {
        const sel = String(edad.value).trim();
        // si la selección es un número (ej. "3","4",...,"16") buscar servicios con al menos un menor de esa edad
        const selNum = Number(sel);
        if (!isNaN(selNum)) {
          result = result?.filter((data) =>
            servicios.value
              ?.filter((d) => d.edad !== null && d.edad === selNum)
              ?.includes(data)
          );
        }
      }

      if (
        localidad?.value !== undefined &&
        localidad?.value !== "" &&
        (filtroFechas?.value === undefined ||
          filtroFechas?.value === null ||
          filtroFechas?.value === "")
      ) {
        // Intersección de registros filtrados por búsqueda y por localidad
        result = result?.filter((data) =>
          servicios.value
            ?.filter((d) => d.localidad == localidad.value)
            ?.includes(data)
        );
      }

      if (
        filtroFechas?.value !== undefined &&
        filtroFechas?.value !== null &&
        filtroFechas?.value !== "" &&
        (localidad?.value === undefined || localidad?.value === "")
      ) {
        // Intersección de registros filtrados por búsqueda y por fechas
        result = result?.filter((data) =>
          servicios.value
            ?.filter((d) => {
              const fechaCreacion = new Date(d.createdOn);
              const fechaInicio: Date = (filtroFechas.value as Date[])[0];
              const fechaFin: Date = (filtroFechas.value as Date[])[1];
              return fechaInicio <= fechaCreacion && fechaFin >= fechaCreacion;
            })
            ?.includes(data)
        );
      }

      if (
        filtroFechas?.value !== undefined &&
        filtroFechas?.value !== null &&
        filtroFechas?.value !== "" &&
        localidad?.value !== undefined &&
        localidad?.value !== ""
      ) {
        // Intersección de registros filtrados por búsqueda, por localidad y por fechas
        result = result?.filter((item) => {
          const fechaCreacion = new Date(item.createdOn);
          const fechaInicio: Date = (filtroFechas.value as Date[])[0];
          const fechaFin: Date = (filtroFechas.value as Date[])[1];

          const fechaInRange =
            fechaInicio <= fechaCreacion && fechaFin >= fechaCreacion;
          const localidadMatch = item.localidad === localidad.value;

          return fechaInRange && localidadMatch;
        });
      }

      // Aplicar filtro por estado (case-insensitive) sobre el resultado final
      if (estado?.value) {
        const estadoFiltro = String(estado.value).toLowerCase();
        result = result?.filter(
          (d) => String(d.estado || "").toLowerCase() === estadoFiltro
        );
      }

      return result;
    });

    const municipiosComarca = computed(() => {
      const isAdmin = !comarcaId;
      if (!isAdmin) {
        return municipiosComarcas.filter(
          (municipio) => municipio.comarcaId === comarcaId
        );
      }
      if (!selectedComarcaId.value || selectedComarcaId.value === "TODAS") {
        return municipiosComarcas;
      }
      return municipiosComarcas.filter(
        (municipio) => municipio.comarcaId === selectedComarcaId.value
      );
    });

    const { result, currentPage, lastPage } = useArrayPagination(
      serviciosListado,
      { pageSize: 100 }
    );

    const handleCurrentPageChange = (page: number) => {
      currentPage.value = page;
    };

    return {
      serviciosListado,
      filteredCollection,
      isAdmin: !comarcaId,
      csvLabels,
      csvFields,
      csvName,
      handleEdit,
      sortIngresos,
      sortEstado,
      search,
      ServicioEstado,
      toLocaleDateString,
      sortChange: ({ column, prop, order }) => {
        sortProps.value = { prop, order, method: column.sortMethod };
      },
      result,
      currentPage,
      lastPage,
      handleCurrentPageChange,
      municipiosComarca,
      localidad,
      filtroFechas,
      estado,
      FormularioEstado,
      // opciones de estado (solo estados)
      estados: [
        {
          value: FormularioEstado.pendiente,
          label: t("estados.PENDIENTE"),
        },
        {
          value: FormularioEstado.listaDeEspera,
          label: t("estados.LISTA_DE_ESPERA"),
        },
        {
          value: FormularioEstado.rechazado,
          label: t("estados.RECHAZADO"),
        },
        {
          value: FormularioEstado.realizadorPorError,
          label: t("estados.REALIZADO_POR_ERRROR"),
        },
        {
          value: FormularioEstado.admitidoNoUsaServicio,
          label: t("estados.ADMITIDO_NO_USA_SERVICIO"),
        },
      ],
      // exponer aquí las opciones y refs relacionados con género/edad
      generos,
      genero,
      edad,
      edadesMenores,
      selectedComarcaId,
      comarcas,
      loadingComarcas,
      onComarcaVisibleChange,
      onComarcaChange,
    };
  },
});
